import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  PlusIcon, 
  BellIcon, 
  Cog6ToothIcon,
  ChartBarIcon,
  CalendarIcon,
  UserGroupIcon,
  DocumentTextIcon,
  ClockIcon,
  CheckCircleIcon,
  ChartBarSquareIcon,
  PencilIcon,
  TrashIcon,
  ViewColumnsIcon,
  ArrowRightOnRectangleIcon,
  ExclamationTriangleIcon,
  TagIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { useProject } from '../../contexts/ProjectContext';
import { useTheme } from '../../contexts/ThemeContext';
import CreateProjectModal from '../projects/CreateProjectModal';
import EditProjectModal from '../projects/EditProjectModal';
import ProjectTasksModal from '../projects/ProjectTasksModal';
import GanttView from '../gantt/GanttView';
import CalendarView from '../calendar/CalendarView';
import KanbanView from '../kanban/KanbanView';
import TeamManagement from '../teams/TeamManagement';
import TasksView from '../tasks/TasksView';
import TagsView from '../tags/TagsView';
import { getStatusLabel, getStatusColorClasses } from '../../utils/statusConfig';
import { ProjectService } from '../../services/ProjectService';
import { DashboardService, formatDashboardData } from '../../services/DashboardService';
import toast from 'react-hot-toast';

const Dashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isTasksModalOpen, setIsTasksModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0); // Key para forzar recarga de componentes hijo
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [showNotificationsMenu, setShowNotificationsMenu] = useState(false);
  const [accountInfo, setAccountInfo] = useState([]);
  const [showTrialEndedModal, setShowTrialEndedModal] = useState(false);
  const { user, logout } = useAuth();
  const { projects, setCurrentProject, loading: projectsLoading, refreshProjects } = useProject();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  // Cargar datos del dashboard al montar el componente
  useEffect(() => {
    loadDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cerrar menús cuando se hace clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showSettingsMenu && !event.target.closest('.settings-menu')) {
        setShowSettingsMenu(false);
      }
      if (showNotificationsMenu && !event.target.closest('.notifications-menu')) {
        setShowNotificationsMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSettingsMenu, showNotificationsMenu]);

  // Recargar datos cuando cambia el tab activo
  useEffect(() => {
    console.log('🔄 Cambiando a vista:', activeTab);
    
    // Incrementar refreshKey para forzar recarga de componentes hijo
    setRefreshKey(prev => prev + 1);
    
    switch (activeTab) {
      case 'overview':
        // Recargar solo datos del dashboard (ya incluye totalProjects, recentProjects, etc)
        loadDashboardData();
        break;
      case 'projects':
        // Recargar lista completa de proyectos
        refreshProjects();
        break;
      case 'kanban':
        // Los datos de kanban se recargan automáticamente por key
        break;
      case 'tasks':
        // Recargar proyectos para mostrar tareas
        refreshProjects();
        break;
      case 'calendar':
        // Los eventos se recargan automáticamente por key
        break;
      case 'gantt':
        // Recargar proyectos para el diagrama de Gantt
        refreshProjects();
        break;
      case 'tags':
        // Las etiquetas se recargan bajo demanda dentro de la vista
        break;
      case 'team':
        // Recargar datos del equipo
        break;
      default:
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]); // Se ejecuta cada vez que cambia activeTab

  const evaluateAccountStatus = (accounts = []) => {
    if (!Array.isArray(accounts) || accounts.length === 0) {
      setShowTrialEndedModal(false);
      return;
    }

    const primaryAccount = accounts[0];
    const planId = Number(primaryAccount?.plan_id);
    const status = (primaryAccount?.status || '').toLowerCase();

    if (planId === 1 && status === 'suspended') {
      setShowTrialEndedModal(true);
    } else {
      setShowTrialEndedModal(false);
    }
  };

  const handleGoToPricing = () => {
    navigate('/pricing');
  };

  const loadDashboardData = async () => {
    setDashboardLoading(true);
    try {
      const [dashboardResult, accountResult] = await Promise.all([
        DashboardService.getDashboardData(),
        DashboardService.getAccountInfo()
      ]);

      if (dashboardResult?.success) {
        const formattedData = formatDashboardData(dashboardResult.data);
        setDashboardData(formattedData);
        console.log('✅ Datos del dashboard cargados:', formattedData);
      } else {
        const errorMessage = dashboardResult?.error || 'Error al cargar datos del dashboard';
        console.error('❌ Error al cargar datos del dashboard:', errorMessage);
        toast.error('Error al cargar datos del dashboard');
      }

      if (accountResult?.success) {
        setAccountInfo(accountResult.account);
        evaluateAccountStatus(accountResult.account);
      } else {
        setAccountInfo([]);
        evaluateAccountStatus([]);
        if (accountResult?.error) {
          console.error('❌ Error al obtener información de la cuenta:', accountResult.error);
        }
      }
    } catch (error) {
      console.error('❌ Error al cargar datos del dashboard:', error);
      toast.error('Error al cargar datos del dashboard');
      setAccountInfo([]);
      evaluateAccountStatus([]);
    } finally {
      setDashboardLoading(false);
    }
  };

  const primaryAccount = Array.isArray(accountInfo) && accountInfo.length > 0 ? accountInfo[0] : null;
  const trialEndDate = primaryAccount?.end_date
    ? new Date(primaryAccount.end_date).toLocaleDateString('es-MX', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    : null;

  // Función para manejar clic en tarjeta de proyecto
  const handleProjectClick = (project) => {
    setSelectedProject(project);
    setIsTasksModalOpen(true);
  };

  // Función para manejar edición de proyecto
  const handleEditProject = (project, e) => {
    e.stopPropagation(); // Evitar que se abra el modal de tareas
    setSelectedProject(project);
    setIsEditModalOpen(true);
  };

  // Función para refrescar proyectos después de editar
  const handleProjectUpdated = () => {
    refreshProjects();
  };

  // Función para manejar proyecto creado
  const handleProjectCreated = () => {
    refreshProjects();
  };

  // Función para manejar actualización de tareas
  const handleTasksUpdated = () => {
    // Recargar datos del dashboard para actualizar métricas y proyectos recientes
    loadDashboardData();
    // También refrescar la lista de proyectos para mantener consistencia
    refreshProjects();
  };

  // Función para eliminar proyecto
  const handleDeleteProject = async (project, e) => {
    e.stopPropagation(); // Evitar que se abra el modal de tareas
    
    // Confirmar eliminación
    const confirmed = window.confirm(`¿Estás seguro de que deseas eliminar el proyecto "${project.name}"?\n\nEsta acción no se puede deshacer y eliminará todas las tareas asociadas.`);
    if (!confirmed) return;

    const loadingToast = toast.loading('Eliminando proyecto...');
    
    try {
      const result = await ProjectService.deleteProject(project.id);
      
      if (result.success) {
        toast.success('Proyecto eliminado exitosamente', { id: loadingToast });
        // Refrescar la lista de proyectos
        refreshProjects();
      } else {
        toast.error(result.error || 'Error al eliminar el proyecto', { id: loadingToast });
      }
    } catch (err) {
      toast.error('Error al eliminar el proyecto', { id: loadingToast });
      console.error('Error deleting project:', err);
    }
  };

  // Métricas desde la API del dashboard
  const metrics = dashboardData ? {
    activeProjects: dashboardData.activeProjects || 0,
    activeTasks: dashboardData.activeTasks || 0,
    totalProjects: dashboardData.totalProjects || 0,
    progress: dashboardData.progress || 0,
  } : {
    activeProjects: 0,
    activeTasks: 0,
    totalProjects: 0,
    progress: 0,
  };

  // Eventos recientes desde la API
  const recentActivities = dashboardData?.recentEvents || [];

  // Fechas importantes desde la API
  const upcomingDeadlines = dashboardData?.upcomingDates || [];

  // Filtrar tabs según el role del usuario
  const allTabs = [
    { id: 'overview', name: 'Resumen', icon: ChartBarIcon },
    { id: 'projects', name: 'Proyectos', icon: DocumentTextIcon },
    { id: 'kanban', name: 'Kanban', icon: ViewColumnsIcon },
    { id: 'tasks', name: 'Tareas', icon: CheckCircleIcon },
    { id: 'team', name: 'Equipo', icon: UserGroupIcon, adminOnly: true }, // Solo para admins
    { id: 'calendar', name: 'Calendario', icon: CalendarIcon },
    { id: 'gantt', name: 'Gantt', icon: ChartBarSquareIcon },
    { id: 'tags', name: 'Etiquetas', icon: TagIcon }
  ];

  // Filtrar tabs: solo mostrar 'team' si el usuario es admin
  const tabs = allTabs.filter(tab => {
    if (tab.adminOnly) {
      return user?.role === 'admin';
    }
    return true;
  });

  const renderOverview = () => (
    <div className="space-y-6">
      {dashboardLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      ) : (
        <>
          {/* Métricas principales */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="card p-6"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Proyectos Activos</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{metrics.activeProjects}</p>
                </div>
                <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900 rounded-lg flex items-center justify-center">
                  <DocumentTextIcon className="h-6 w-6 text-primary-600" />
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="card p-6"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Tareas Activas</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{metrics.activeTasks}</p>
                </div>
                <div className="w-12 h-12 bg-warning-100 dark:bg-warning-900 rounded-lg flex items-center justify-center">
                  <CheckCircleIcon className="h-6 w-6 text-warning-600" />
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="card p-6"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Progreso</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{metrics.progress}%</p>
                </div>
                <div className="w-12 h-12 bg-success-100 dark:bg-success-900 rounded-lg flex items-center justify-center">
                  <ChartBarIcon className="h-6 w-6 text-success-600" />
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="card p-6"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Proyectos</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{metrics.totalProjects}</p>
                </div>
                <div className="w-12 h-12 bg-secondary-100 dark:bg-secondary-900 rounded-lg flex items-center justify-center">
                  <ClockIcon className="h-6 w-6 text-secondary-600" />
                </div>
              </div>
            </motion.div>
          </div>

      {/* Proyectos recientes y actividades */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Proyectos recientes */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.5 }}
          className="card p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Proyectos Recientes</h3>
            <button 
              className="text-primary-600 hover:text-primary-700 text-sm font-medium"
              onClick={() => setActiveTab('projects')}
            >
              Ver todos
            </button>
          </div>
          <div className="space-y-4">
            {dashboardData?.recentProjects && dashboardData.recentProjects.length > 0 ? (
              dashboardData.recentProjects.map((project) => (
                <div 
                  key={project.id} 
                  className="flex items-center space-x-3 p-3 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors cursor-pointer"
                  onClick={() => handleProjectClick(project)}
                >
                  <div className={`w-10 h-10 bg-${project.color}-100 dark:bg-${project.color}-900 rounded-lg flex items-center justify-center flex-shrink-0`}>
                    <span className="text-lg">{project.icon}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-gray-900 dark:text-white truncate">{project.name}</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 truncate">{project.description}</p>
                    <div className="mt-2">
                      <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                        <span>Progreso: {project.progress || 0}%</span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColorClasses(project.status)}`}>
                          {getStatusLabel(project.status)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                        <span>Tareas: {project.tasksCount || 0}</span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                        <div 
                          className={`bg-${project.color}-500 h-1.5 rounded-full transition-all duration-300`}
                          style={{ width: `${project.progress || 0}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <p>No hay proyectos recientes</p>
                <button 
                  onClick={loadDashboardData}
                  className="text-primary-600 hover:text-primary-700 text-sm font-medium mt-2"
                >
                  Recargar datos
                </button>
              </div>
            )}
          </div>
        </motion.div>

        {/* Actividades recientes */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.6 }}
          className="card p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Eventos Recientes</h3>
          </div>
          <div className="space-y-4">
            {recentActivities.length > 0 ? (
              recentActivities.map((event) => (
                <div key={event.id} className="flex items-start space-x-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900 rounded-lg flex items-center justify-center flex-shrink-0">
                    <DocumentTextIcon className="h-5 w-5 text-primary-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {event.title}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {event.description}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">{event.dateTime || event.date}</p>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-4 text-gray-500">
                No hay eventos recientes
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Próximas fechas importantes */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
        className="card p-6"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Próximas Fechas Importantes</h3>
          <button 
            className="text-primary-600 hover:text-primary-700 text-sm font-medium"
            onClick={() => setActiveTab('calendar')}
          >
            Ver calendario
          </button>
        </div>
        <div className="space-y-3">
          {upcomingDeadlines.length > 0 ? (
            upcomingDeadlines.map((event) => (
              <div key={event.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                <div className="flex items-center space-x-3 flex-1 min-w-0">
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                    event.status === 'active' ? 'bg-success-500' :
                    event.status === 'completed' ? 'bg-blue-500' :
                    event.status === 'pending' ? 'bg-warning-500' :
                    event.status === 'cancelled' ? 'bg-danger-500' :
                    'bg-gray-500'
                  }`} />
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-gray-900 dark:text-white truncate">{event.name}</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {event.initialDateTime || event.initialDate}
                    </p>
                  </div>
                </div>
                <div className={`px-2.5 py-1 rounded-full text-xs font-medium flex-shrink-0 ml-2 ${
                  event.status === 'active' 
                    ? 'bg-success-100 dark:bg-success-900 text-success-800 dark:text-success-200'
                    : event.status === 'completed'
                    ? 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200'
                    : event.status === 'pending'
                    ? 'bg-warning-100 dark:bg-warning-900 text-warning-800 dark:text-warning-200'
                    : event.status === 'cancelled'
                    ? 'bg-danger-100 dark:bg-danger-900 text-danger-800 dark:text-danger-200'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                }`}>
                  {event.status === 'active' ? 'Activo' :
                   event.status === 'completed' ? 'Completado' :
                   event.status === 'pending' ? 'Pendiente' :
                   event.status === 'cancelled' ? 'Cancelado' :
                   event.status}
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-4 text-gray-500">
              No hay fechas importantes próximas
            </div>
          )}
        </div>
      </motion.div>
        </>
      )}
    </div>
  );

  const getTagChipStyles = (color) => {
    const hex = color?.startsWith('#') ? color : `#${color || '4B5563'}`;
    return {
      backgroundColor: `${hex}20`,
      color: hex
    };
  };

  const renderProjects = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Mis Proyectos</h2>
        <button 
          className="btn-primary flex items-center space-x-2"
          onClick={() => setIsCreateModalOpen(true)}
        >
          <PlusIcon className="h-4 w-4" />
          <span>Nuevo Proyecto</span>
        </button>
      </div>

      {projectsLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      ) : projects.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((project) => (
            <motion.div
              key={project.id}
              whileHover={{ scale: 1.02 }}
              className="card-hover p-6 cursor-pointer relative"
              onClick={() => handleProjectClick(project)}
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`w-12 h-12 bg-${project.color}-100 dark:bg-${project.color}-900 rounded-lg flex items-center justify-center`}>
                  <span className="text-2xl">{project.icon}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="text-right">
                    <div className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColorClasses(project.status)}`}>
                      {getStatusLabel(project.status)}
                    </div>
                    <div className="text-xs text-gray-400 mt-1">
                      {project.methodology}
                    </div>
                  </div>
                  {/* Botones de acción */}
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={(e) => handleEditProject(project, e)}
                      className="p-1.5 rounded-lg bg-white dark:bg-gray-700 hover:bg-blue-100 dark:hover:bg-blue-600 transition-colors shadow-sm"
                      title="Editar proyecto"
                    >
                      <PencilIcon className="h-3.5 w-3.5 text-blue-600 dark:text-blue-300" />
                    </button>
                    <button
                      onClick={(e) => handleDeleteProject(project, e)}
                      className="p-1.5 rounded-lg bg-white dark:bg-gray-700 hover:bg-red-100 dark:hover:bg-red-600 transition-colors shadow-sm"
                      title="Eliminar proyecto"
                    >
                      <TrashIcon className="h-3.5 w-3.5 text-red-600 dark:text-red-300" />
                    </button>
                  </div>
                </div>
              </div>
              
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                {project.name}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
                {project.description}
              </p>

            {project.tags && project.tags.length > 0 && (
              <div className="mb-4">
                <div className="flex items-center text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2">
                  <TagIcon className="h-4 w-4 mr-1" />
                  Etiquetas
                </div>
                <div className="flex flex-wrap gap-2">
                  {project.tags.map((tag) => (
                    <span
                      key={`${project.id}-tag-${tag.id}`}
                      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                      style={getTagChipStyles(tag.color)}
                    >
                      {tag.name}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {project.participants && project.participants.length > 0 && (
              <div className="mb-4">
                <div className="flex items-center text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2">
                  <UserGroupIcon className="h-4 w-4 mr-1" />
                  Participantes ({project.participants.length})
                </div>
                <div className="flex flex-wrap gap-2">
                  {project.participants.map((participant) => (
                    <div
                      key={`${project.id}-participant-${participant.id}`}
                      className="inline-flex items-center px-2 py-1 rounded-full bg-blue-50 dark:bg-blue-900/20 text-xs text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800"
                      title={participant.email}
                    >
                      <span className="font-medium">
                        {participant.name} {participant.last_name}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
              
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Progreso</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {project.progress || 0}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div 
                    className={`bg-${project.color}-500 h-2 rounded-full transition-all duration-300`}
                    style={{ width: `${project.progress || 0}%` }}
                  />
                </div>
              </div>
              
              <div className="flex items-center justify-between mt-4 text-sm text-gray-500">
                <span>Tareas: {project.tasksCount || 0}</span>
                <span>ID: {project.id}</span>
              </div>
              
              {project.initialDate && project.finalDate && (
                <div className="mt-2 text-xs text-gray-500">
                  <div>Inicio: {new Date(project.initialDate).toLocaleDateString()}</div>
                  <div>Fin: {new Date(project.finalDate).toLocaleDateString()}</div>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <div className="w-24 h-24 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
            <DocumentTextIcon className="h-12 w-12 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            No hay proyectos creados
          </h3>
        </div>
      )}
    </div>
  );

  return (
    <>
      {showTrialEndedModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.25 }}
            className="max-w-md w-full bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-warning-200 dark:border-warning-800 p-8 text-center"
          >
            <div className="mx-auto mb-6 w-16 h-16 bg-warning-100 dark:bg-warning-900 rounded-full flex items-center justify-center">
              <ExclamationTriangleIcon className="h-10 w-10 text-warning-600 dark:text-warning-200" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Tu prueba del plan Pro terminó
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
              Para seguir usando TeamiFlow sin interrupciones, elige uno de nuestros planes disponibles.
            </p>
            {trialEndDate && (
              <p className="text-xs text-gray-500 dark:text-gray-500 mb-6">
                Acceso finalizado el {trialEndDate}.
              </p>
            )}
            <button
              type="button"
              onClick={handleGoToPricing}
              className="w-full btn-primary py-3 text-base font-semibold"
            >
              Elegir un plan
            </button>
          </motion.div>
        </div>
      )}

      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        {/* Header */}
        <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center space-x-4">
                <div className="w-8 h-8 bg-gradient-to-r from-primary-600 to-secondary-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-sm">P</span>
                </div>
                <h1 className="text-xl font-bold text-gray-900 dark:text-white">TeamiFlow</h1>
              </div>

              <div className="flex items-center space-x-4">
                <button
                  onClick={toggleTheme}
                  className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  {theme === 'light' ? '🌙' : '☀️'}
                </button>
                
                <div className="relative notifications-menu">
                  <button 
                    onClick={() => setShowNotificationsMenu(!showNotificationsMenu)}
                    className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors relative"
                  >
                    <BellIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                    {dashboardData?.upcomingDates && dashboardData.upcomingDates.length > 0 && (
                      <span className="absolute -top-1 -right-1 w-3 h-3 bg-danger-500 rounded-full"></span>
                    )}
                  </button>
                  
                  {/* Dropdown de notificaciones */}
                  {showNotificationsMenu && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="absolute right-0 top-full mt-2 w-80 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2 z-50"
                    >
                      <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                          Próximas Fechas Importantes
                        </h3>
                      </div>
                      <div className="max-h-64 overflow-y-auto">
                        {dashboardData?.upcomingDates && dashboardData.upcomingDates.length > 0 ? (
                          dashboardData.upcomingDates.map((event) => (
                            <div key={event.id} className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                              <div className="flex items-start justify-between">
                                <div className="flex items-start space-x-3 flex-1 min-w-0">
                                  <div className={`w-2 h-2 rounded-full flex-shrink-0 mt-2 ${
                                    event.status === 'active' ? 'bg-success-500' :
                                    event.status === 'completed' ? 'bg-blue-500' :
                                    event.status === 'pending' ? 'bg-warning-500' :
                                    event.status === 'cancelled' ? 'bg-danger-500' :
                                    'bg-gray-500'
                                  }`} />
                                  <div className="flex-1 min-w-0">
                                    <h4 className="font-medium text-gray-900 dark:text-white truncate text-sm">
                                      {event.name}
                                    </h4>
                                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                      {event.initialDateTime || event.initialDate}
                                    </p>
                                  </div>
                                </div>
                                <div className={`px-2 py-1 rounded-full text-xs font-medium flex-shrink-0 ml-2 ${
                                  event.status === 'active' 
                                    ? 'bg-success-100 dark:bg-success-900 text-success-800 dark:text-success-200'
                                    : event.status === 'completed'
                                    ? 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200'
                                    : event.status === 'pending'
                                    ? 'bg-warning-100 dark:bg-warning-900 text-warning-800 dark:text-warning-200'
                                    : event.status === 'cancelled'
                                    ? 'bg-danger-100 dark:bg-danger-900 text-danger-800 dark:text-danger-200'
                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                                }`}>
                                  {event.status === 'active' ? 'Activo' :
                                   event.status === 'completed' ? 'Completado' :
                                   event.status === 'pending' ? 'Pendiente' :
                                   event.status === 'cancelled' ? 'Cancelado' :
                                   event.status}
                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="px-4 py-8 text-center">
                            <BellIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              No tienes notificaciones
                            </p>
                          </div>
                        )}
                      </div>
                      {dashboardData?.upcomingDates && dashboardData.upcomingDates.length > 0 && (
                        <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-700">
                          <button 
                            onClick={() => {
                              setShowNotificationsMenu(false);
                              setActiveTab('calendar');
                            }}
                            className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                          >
                            Ver calendario completo
                          </button>
                        </div>
                      )}
                    </motion.div>
                  )}
                </div>

                <div className="flex items-center space-x-3">
                  <img
                    src={user?.avatar}
                    alt={user?.name}
                    className="w-8 h-8 rounded-full"
                  />
                  <div className="hidden md:block">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {user?.name}
                    </div>
                    <div className="text-xs text-gray-500">{user?.role}</div>
                  </div>
                </div>

                <div className="relative settings-menu">
                  <button
                    onClick={() => setShowSettingsMenu(!showSettingsMenu)}
                    className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  >
                    <Cog6ToothIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                  </button>
                  
                  {/* Menú desplegable */}
                  {showSettingsMenu && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="absolute right-0 top-full mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2 z-50"
                    >
                      <button
                        onClick={() => {
                          setShowSettingsMenu(false);
                          logout();
                        }}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
                      >
                        <ArrowRightOnRectangleIcon className="h-4 w-4" />
                        <span>Cerrar sesión</span>
                      </button>
                    </motion.div>
                  )}
                </div>
              </div>
            </div>
          </div>
          </header>

          {/* Navigation Tabs */}
          <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <nav className="flex space-x-8">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === tab.id
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                    }`}
                  >
                    <tab.icon className="h-4 w-4" />
                    <span>{tab.name}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              {activeTab === 'overview' && renderOverview()}
              {activeTab === 'projects' && renderProjects()}
              {activeTab === 'kanban' && <KanbanView key={`kanban-${refreshKey}`} />}
              {activeTab === 'tasks' && <TasksView key={`tasks-${refreshKey}`} />}
              {activeTab === 'team' && <TeamManagement key={`team-${refreshKey}`} />}
              {activeTab === 'calendar' && <CalendarView key={`calendar-${refreshKey}`} />}
              {activeTab === 'gantt' && <GanttView key={`gantt-${refreshKey}`} />}
              {activeTab === 'tags' && <TagsView key={`tags-${refreshKey}`} />}
            </motion.div>
          </main>
          
          {/* Modal de Creación de Proyecto */}
          <CreateProjectModal 
            isOpen={isCreateModalOpen}
            onClose={() => setIsCreateModalOpen(false)}
            onProjectCreated={handleProjectCreated}
          />

          {/* Modal de Edición de Proyecto */}
          <EditProjectModal
            isOpen={isEditModalOpen}
            onClose={() => setIsEditModalOpen(false)}
            project={selectedProject}
            onProjectUpdated={handleProjectUpdated}
          />
          
          {/* Modal de Tareas del Proyecto */}
          <ProjectTasksModal 
            isOpen={isTasksModalOpen}
            onClose={() => setIsTasksModalOpen(false)}
            project={selectedProject}
            onTasksUpdated={handleTasksUpdated}
          />
        </div>
      </>
    );
};

export default Dashboard;
